import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { User, MapPin, Trophy, Star, Shield, Flame, ArrowLeft, Loader2, Edit3, Check, X, Crown, BarChart3, Bell, Heart, LifeBuoy, Lock, Trash2, FileText, Cookie, Flag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import SocialShareButtons from "../components/SocialShareButtons";
import { subscribeToPush, unsubscribeFromPush, isPushSupported, getPushUnavailableReasonKey } from "../utils/pushManager";
import { formatTranslation, getRankLabel } from "../utils/ranks";
import { getBadgeName, getBadgeDescription } from "../utils/badges";
import { isAppleSignInSupported } from "../utils/appleIdentity";

import { API } from "../config";

function TrustBar({ score }) {
  const color = score >= 80 ? "#66BB6A" : score >= 50 ? "#42A5F5" : score >= 20 ? "#FFA726" : "#FF5252";
  return (
    <div className="w-full bg-[#F8F9FA] rounded-full h-2.5">
      <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: color }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#F8F9FA] rounded-xl p-4 text-center">
      <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
      <p className="text-2xl font-black text-[#2B2D42]">{value}</p>
      <p className="text-xs text-[#8D99AE]">{label}</p>
    </div>
  );
}

export default function ProfilePage() {
  const { user, checkAuth, linkGoogle, linkApple } = useAuth();
  const { t, isRtl, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [notificationsOn, setNotificationsOn] = useState(() => localStorage.getItem("caca_notifications") !== "off");
  const showAppleLink = isAppleSignInSupported();

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users/profile`, { withCredentials: true });
      setProfile(data);
      setNewUsername(data.username || "");
      // Check actual push subscription status
      try {
        const { data: pushStatus } = await axios.get(`${API}/push/status`, { withCredentials: true });
        setNotificationsOn(pushStatus.subscribed);
      } catch { /* ignore */ }
    } catch { navigate("/login"); }
    finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    fetchProfile();
  }, [fetchProfile, navigate, user]);

  useEffect(() => {
    const linked = searchParams.get("apple_linked");
    const appleError = searchParams.get("apple_error");
    if (!linked && !appleError) return;
    if (linked === "1") {
      toast.success(t("profileUi.appleLinked"));
    } else if (appleError) {
      toast.error(appleError);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("apple_linked");
    next.delete("apple_error");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const handleSaveUsername = async () => {
    if (!newUsername.trim() || newUsername.length < 3) { toast.error(t("profileUi.usernameTooShort")); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/users/username`, { username: newUsername.trim() }, { withCredentials: true });
      toast.success(t("profileUi.usernameUpdated"));
      setEditingUsername(false);
      await checkAuth();
      fetchProfile();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleToggleNotifications = async () => {
    const supported = await isPushSupported();
    if (!supported) {
      toast.error(t(getPushUnavailableReasonKey()));
      return;
    }
    if (notificationsOn) {
      await unsubscribeFromPush();
      setNotificationsOn(false);
      toast.success(t("mapUi.pushDisabled"));
    } else {
      try {
        await subscribeToPush(null);
        setNotificationsOn(true);
        toast.success(t("mapUi.pushEnabled"));
      } catch (err) {
        if (err.message === "permission_denied") {
          toast.error(t("profileUi.browserPermissionDenied"));
        } else if (err.message === "native_push_disabled_for_build") {
          toast.error(t(getPushUnavailableReasonKey()));
        } else {
          toast.error(t("mapUi.pushError"));
        }
      }
    }
  };

  const getSharePayload = async () => {
    const { data } = await axios.get(`${API}/users/${user._id || user.id}/share`, { withCredentials: true });
    const displayName = profile?.username || profile?.name || user?.username || user?.name || t("mapUi.userFallback");
    return {
      title: formatTranslation(t, "rankUi.shareTitle", { name: displayName }),
      text: `${formatTranslation(t, "rankUi.profileShareText", {
        name: displayName,
        rank: getRankLabel(data.rank_key || data.rank || profile?.rank_key || profile?.rank, t),
        score: data.total_score ?? profile?.total_score ?? 0,
        reports: data.report_count ?? profile?.report_count ?? 0,
        badges: data.badge_count ?? profile?.badges_count ?? 0,
      })}\n\n${formatTranslation(t, "rankUi.downloadText", {
        ios: data.app_store_url,
        android: data.play_store_url,
      })}`,
      url: data.url || data.app_url,
    };
  };

  const handleLinkGoogle = async (credential) => {
    try {
      const result = await linkGoogle(credential);
      toast.success(result.message || t("profileUi.googleLinked"));
      await fetchProfile();
    } catch (err) {
      const detail = err.response?.data?.detail?.message || err.response?.data?.detail || t("loginUi.googleError");
      toast.error(detail);
      throw err;
    }
  };

  const isGoogleLinked = Boolean(profile?.linked_accounts?.google);
  const isAppleLinked = Boolean(profile?.linked_accounts?.apple);

  const handleLinkApple = async () => {
    try {
      const result = await linkApple();
      if (result?.redirected) {
        return;
      }
      toast.success(result.message || t("profileUi.appleLinked"));
      await fetchProfile();
    } catch (err) {
      if (err?.message === "Apple sign-in was cancelled") {
        return;
      }
      const detail = err.response?.data?.detail?.message || err.response?.data?.detail || t("loginUi.appleError");
      toast.error(detail);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" /></div>;

  const trustTierKey = (profile?.trust_score || 50) >= 80 ? "trusted" : (profile?.trust_score || 50) >= 50 ? "normal" : (profile?.trust_score || 50) >= 20 ? "low" : "restricted";
  const trustTiers = [
    { key: "restricted", min: 0, max: 19 },
    { key: "low", min: 20, max: 49 },
    { key: "normal", min: 50, max: 79 },
    { key: "trusted", min: 80, max: 100 },
  ];
  const scoreRows = [
    { action: t("profileUi.scoreReport"), pts: "+10", extra: t("profileUi.scorePhotoDescription") },
    { action: t("profileUi.scoreCorrectValidation"), pts: "+4", extra: t("profileUi.scoreFirstFive") },
    { action: t("profileUi.scoreVerifiedReport"), pts: "+5", extra: "" },
    { action: t("profileUi.scoreUpvoteReceived"), pts: "+2", extra: "" },
    { action: t("profileUi.scoreStreak"), pts: "+15 / +30 / +50", extra: "" },
  ];

  return (
    <div className={`min-h-screen bg-[#F8F9FA] ${isRtl ? 'rtl' : 'ltr'}`} data-testid="profile-page">
      <div className="ios-safe-header p-4 flex justify-between items-center">
        <Button variant="ghost" onClick={() => navigate("/")} className="text-[#8D99AE]" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />{t("backToMap")}
        </Button>
        <LanguageSelector />
      </div>

      <div className="max-w-lg mx-auto px-4 pb-8">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4 text-center">
          <div className="w-20 h-20 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-3">
            <User className="w-10 h-10 text-white" />
          </div>

          {/* Username */}
          {editingUsername ? (
            <div className="flex items-center gap-2 justify-center mb-2">
              <Input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className="max-w-[200px] text-center" maxLength={20} data-testid="username-edit-input" />
              <Button size="sm" variant="ghost" onClick={handleSaveUsername} disabled={saving} className="text-[#66BB6A]"><Check className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingUsername(false)} className="text-[#FF5252]"><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 justify-center mb-1">
              <h1 className="text-xl font-black text-[#2B2D42]" style={{ fontFamily: 'Nunito, sans-serif' }}>{profile?.username || profile?.name || t("mapUi.userFallback")}</h1>
              <button onClick={() => setEditingUsername(true)} className="text-[#8D99AE] hover:text-[#FF6B6B]" data-testid="edit-username-btn"><Edit3 className="w-4 h-4" /></button>
            </div>
          )}

          {/* Rank */}
          <div className="flex items-center justify-center gap-2 mb-1">
            <Crown className="w-4 h-4 text-[#FF6B6B]" />
            <span className="text-sm font-bold text-[#FF6B6B] leading-tight max-w-[280px]">{getRankLabel(profile?.rank_key || profile?.rank, t)}</span>
          </div>
          <p className="text-xs text-[#8D99AE]">{formatTranslation(t, "rankUi.levelPercentile", { level: profile?.level || 1, percentile: profile?.rank_percentile || 0 })}</p>

          {/* Subscription badge */}
          {user?.subscription_active ? (
            <Badge className="mt-2 bg-[#FF6B6B]"><Star className="w-3 h-3 mr-1 fill-white" />Premium</Badge>
          ) : (
            <Button size="sm" variant="outline" onClick={() => navigate("/subscribe")} className="mt-2 text-[#FF6B6B] border-[#FF6B6B]" data-testid="upgrade-btn">
              <Star className="w-3 h-3 mr-1" />{t("profileUi.upgradePremium")}
            </Button>
          )}

          <SocialShareButtons
            className="mt-2"
            prefix="profile-share"
            label={t("profileUi.shareProfile")}
            loadShareData={getSharePayload}
            onCopied={() => toast.success(t("rankUi.profileCopied"))}
            onError={() => toast.error(t("profileUi.shareError"))}
          />
          <Button size="sm" variant="ghost" onClick={() => navigate("/impact")} className="mt-1 text-[#66BB6A]" data-testid="view-impact-btn">
            <Heart className="w-4 h-4 mr-1" />{t("profileUi.communityImpact")}
          </Button>
        </div>

        {/* Score Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <div className="text-center mb-4">
            <p className="text-5xl font-black text-[#2B2D42]">{profile?.total_score || 0}</p>
            <p className="text-sm text-[#8D99AE]">{t("profileUi.totalPoints")}</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard icon={MapPin} label={t("profileUi.reports")} value={profile?.report_count || 0} color="#FF6B6B" />
            <StatCard icon={BarChart3} label={t("profileUi.votes")} value={profile?.vote_count || 0} color="#42A5F5" />
            <StatCard icon={Flame} label={t("profileUi.streak")} value={`${profile?.streak_days || 0}${t("mapUi.dayShort")}`} color="#FFA726" />
          </div>
          {profile?.accuracy_rate !== undefined && (
            <div className="flex gap-3 mt-3">
              <div className="flex-1 bg-[#F8F9FA] rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-[#66BB6A]">{profile.accuracy_rate}%</p>
                <p className="text-xs text-[#8D99AE]">{t("profileUi.accuracy")}</p>
              </div>
              <div className="flex-1 bg-[#F8F9FA] rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-[#42A5F5]">{profile.impact_score || 0}</p>
                <p className="text-xs text-[#8D99AE]">{t("profileUi.impact")}</p>
              </div>
            </div>
          )}
        </div>

        {/* Badges */}
        {profile?.badges && profile.badges.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
            <h3 className="font-bold text-[#2B2D42] mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#FFA726]" />{t("profileUi.badges")} ({profile.badges_count || 0})
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {profile.badges.map((badge) => (
                <div key={badge.id} className="bg-[#FFA726]/10 rounded-xl p-3 text-center">
                  <div className="w-8 h-8 bg-[#FFA726] rounded-full flex items-center justify-center mx-auto mb-1">
                    <Star className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-xs font-bold text-[#2B2D42]">{getBadgeName(badge, language)}</p>
                  <p className="text-xs text-[#8D99AE]">{getBadgeDescription(badge, language)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trust Score */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#42A5F5]" />
              <span className="font-bold text-[#2B2D42]">{t("profileUi.trustLevel")}</span>
            </div>
            <div className="text-right">
              <span className="text-lg font-black text-[#2B2D42]">{profile?.trust_score || 50}</span>
              <span className="text-xs text-[#8D99AE]"> / 100</span>
            </div>
          </div>
          <TrustBar score={profile?.trust_score || 50} />
          <p className="text-xs text-[#8D99AE] mt-2">{t("profileUi.status")}: <span className="font-medium">{t(`profileUi.trustTiers.${trustTierKey}`)}</span></p>
          <div className="grid grid-cols-4 gap-1 mt-3">
            {trustTiers.map((tier) => (
              <div key={tier.key} className={`text-center py-1 rounded text-xs ${(profile?.trust_score || 50) >= tier.min && (profile?.trust_score || 50) <= tier.max ? 'bg-[#FF6B6B]/10 text-[#FF6B6B] font-bold' : 'text-[#8D99AE]'}`}>
                {t(`profileUi.trustTiers.${tier.key}`)}
              </div>
            ))}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-[#2B2D42]">{t("profileUi.loginMethods")}</h3>
              <p className="text-xs text-[#8D99AE] mt-1">{t("profileUi.loginMethodsDesc")}</p>
            </div>
            {isGoogleLinked && (
              <Badge className="bg-[#66BB6A]">{t("profileUi.googleLinked")}</Badge>
            )}
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3">
              <span className="text-sm font-medium text-[#2B2D42]">{t("profileUi.passwordLogin")}</span>
              <span className="text-xs text-[#8D99AE]">
                {profile?.linked_accounts?.password ? t("profileUi.linked") : t("profileUi.notLinked")}
              </span>
            </div>
            {!isGoogleLinked && (
              <GoogleSignInButton
                onCredential={handleLinkGoogle}
                onError={(error) => toast.error(error.message || t("loginUi.googleError"))}
                text="continue_with"
                context="use"
                testId="google-link-btn"
              />
            )}
            {showAppleLink && (
              <div className="flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3">
                <span className="text-sm font-medium text-[#2B2D42]">Apple</span>
                {isAppleLinked ? (
                  <Badge className="bg-[#66BB6A]">{t("profileUi.appleLinked")}</Badge>
                ) : (
                  <button type="button" onClick={handleLinkApple} className="rounded-lg bg-black px-3 py-2 text-xs font-medium text-white hover:bg-gray-900" data-testid="apple-link-btn">
                    {t("profileUi.linkApple")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#42A5F5]" />
              <span className="font-bold text-[#2B2D42]">{t("profileUi.notifications")}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleNotifications}
                className={`relative w-12 h-6 rounded-full transition-colors ${notificationsOn ? "bg-[#66BB6A]" : "bg-[#8D99AE]/30"}`}
                data-testid="notification-toggle"
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${notificationsOn ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-xl border-[#42A5F5] text-[#42A5F5]"
                onClick={() => navigate("/settings/notifications")}
                data-testid="notification-settings-link"
              >
                {language === "en" ? "Manage" : "Gestionar"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-[#8D99AE] mt-2">{t("profileUi.notificationsDesc")}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h3 className="font-bold text-[#2B2D42] mb-3">{t("legalUi.legalAndHelp")}</h3>
          <div className="rounded-xl bg-[#F8F9FA] px-4 py-3 text-sm text-[#5C677D] mb-3">
            Caca Radar es una herramienta privada, independiente y no oficial. No representa, sustituye ni está afiliada a ningún ayuntamiento,
            administración pública ni entidad gubernamental.
          </div>
          <div className="space-y-2">
            <button
              onClick={() => navigate("/help")}
              className="w-full flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3 text-left"
              data-testid="profile-help-link"
            >
              <span className="flex items-center gap-2 text-[#2B2D42] font-medium">
                <LifeBuoy className="w-4 h-4 text-[#42A5F5]" />
                {t("legalUi.help")}
              </span>
            </button>
            <button
              onClick={() => navigate("/privacy")}
              className="w-full flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3 text-left"
              data-testid="profile-privacy-link"
            >
              <span className="flex items-center gap-2 text-[#2B2D42] font-medium">
                <Lock className="w-4 h-4 text-[#66BB6A]" />
                {t("legalUi.privacyPolicy")}
              </span>
            </button>
            <button
              onClick={() => navigate("/terms")}
              className="w-full flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3 text-left"
              data-testid="profile-terms-link"
            >
              <span className="flex items-center gap-2 text-[#2B2D42] font-medium">
                <FileText className="w-4 h-4 text-[#FFA726]" />
                {t("helpUi.termsTitle")}
              </span>
            </button>
            <button
              onClick={() => navigate("/cookies")}
              className="w-full flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3 text-left"
              data-testid="profile-cookies-link"
            >
              <span className="flex items-center gap-2 text-[#2B2D42] font-medium">
                <Cookie className="w-4 h-4 text-[#42A5F5]" />
                {t("legalUi.cookiesPolicy")}
              </span>
            </button>
            <button
              onClick={() => navigate("/community")}
              className="w-full flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3 text-left"
              data-testid="profile-community-link"
            >
              <span className="flex items-center gap-2 text-[#2B2D42] font-medium">
                <Flag className="w-4 h-4 text-[#FF6B6B]" />
                {t("legalUi.communityGuidelines")}
              </span>
            </button>
            <button
              onClick={() => navigate("/delete-account")}
              className="w-full flex items-center justify-between rounded-xl bg-[#F8F9FA] px-4 py-3 text-left"
              data-testid="profile-delete-account-link"
            >
              <span className="flex items-center gap-2 text-[#2B2D42] font-medium">
                <Trash2 className="w-4 h-4 text-[#FF6B6B]" />
                {t("helpUi.deleteAccountLink")}
              </span>
            </button>
          </div>
          <p className="text-xs text-[#8D99AE] mt-3">
            Puedes solicitar la eliminación de tu cuenta desde esta sección o en la página web de borrado de cuenta.
          </p>
        </div>

        {/* How scoring works */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold text-[#2B2D42] mb-3">{t("profileUi.scoringTitle")}</h3>
          <div className="space-y-2 text-sm">
            {scoreRows.map(({ action, pts, extra }) => (
              <div key={action} className="flex justify-between items-center py-1.5 border-b border-[#F8F9FA] last:border-0">
                <span className="text-[#2B2D42]">{action}</span>
                <div className="text-right">
                  <span className="font-bold text-[#66BB6A]">{pts}</span>
                  {extra && <span className="text-xs text-[#8D99AE] block">{extra}</span>}
                </div>
              </div>
            ))}
            {user?.subscription_active && (
              <div className="bg-[#FF6B6B]/5 rounded-lg p-2 mt-2 text-center">
                <Star className="w-4 h-4 text-[#FF6B6B] inline mr-1" />
                <span className="text-xs text-[#FF6B6B] font-medium">{t("profileUi.premiumMultiplier")}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
